import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { InputRef, Typography } from 'antd';
import { t } from '../../../../utils';
import { ClearSignatureButton } from '../ClearSignatureButton';
import {
	EMPTY_TEXT,
	MAX_FONT_SIZE,
	MIN_FONT_SIZE,
	SignatureAreaDimensions,
	SignatureState,
	SignatureTab,
} from '../constants';
import { SaveSignatureEvent } from '../SaveSignatureEvent';
import {
	HiddenResizer,
	StyledSignatureInput,
	StyledSignatureInputWrapper,
	StyledSignatureTypeWrapper,
} from './ESignatureTextPad.styled';

const { Paragraph } = Typography;

type ESignatureTextPadProps = {
	penColor: string;
	saveSignature: (url: string, data: string, text: string) => void;
	savedSignatureText: string;
	isSignatureSigned: SignatureState;
	setIsSignatureSigned: (value: SignatureState) => void;
};

export const ESignatureTextPad = ({
	penColor,
	saveSignature,
	savedSignatureText,
	isSignatureSigned,
	setIsSignatureSigned,
}: ESignatureTextPadProps) => {
	const [signatureText, setSignatureText] = useState(savedSignatureText);
	const [fontSize, setFontSize] = useState(MAX_FONT_SIZE);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const elementRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<InputRef>(null);

	const resizeText = useCallback(() => {
		const maxWidth = inputRef.current.input.clientWidth;
		const element = elementRef.current;

		setTimeout(() => {
			element.style.fontSize = MAX_FONT_SIZE + 'px';
			while (
				element.getBoundingClientRect().width > maxWidth &&
				parseInt(element.style.fontSize, 10) > MIN_FONT_SIZE
			) {
				element.style.fontSize = parseInt(element.style.fontSize, 10) - 1 + 'px';
			}
			setFontSize(parseInt(element.style.fontSize, 10));
		}, 0);
	}, []);

	const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.currentTarget;
		setSignatureText(value);
		if (value) {
			setIsSignatureSigned(SignatureState.SIGNED);
		} else {
			setIsSignatureSigned(SignatureState.NOT_SIGNED);
		}
		resizeText();
	};

	const clearSignature = () => {
		setSignatureText(EMPTY_TEXT);
		setIsSignatureSigned(SignatureState.NOT_SIGNED);
	};

	useEffect(() => {
		const canvasElement = canvasRef.current;
		const inputElement = inputRef.current;

		const onSaveEventHandler: EventListener = (event: Event) => {
			if ((event as CustomEvent).detail.signatureType !== SignatureTab.TYPED) {
				return;
			}
			if (canvasElement && inputElement) {
				const width = canvasElement.width;
				const height = canvasElement.height;
				const font = `${fontSize}px 'Cedarville Cursive', 'cursive'`;
				const ctx = canvasElement.getContext('2d');

				ctx.clearRect(0, 0, width, height);
				ctx.restore();
				ctx.font = font;
				ctx.textBaseline = 'middle';
				ctx.fillStyle = penColor;
				ctx.fillText(inputElement.input.value, 0, height / 2);
				ctx.save();
				const signatureDataUrl = canvasElement.toDataURL('image/png');
				saveSignature(signatureDataUrl, null, inputElement.input.value);
			}
		};

		SaveSignatureEvent.registerHandler(onSaveEventHandler);

		if (elementRef.current) {
			elementRef.current.textContent = savedSignatureText;
			resizeText();
		}

		return () => {
			SaveSignatureEvent.unRegisterHandler(onSaveEventHandler);
		};
	}, [savedSignatureText, saveSignature, fontSize, resizeText, penColor]);

	return (
		<StyledSignatureTypeWrapper
			vertical
			justify="center"
			data-testid="signature-text-pad"
		>
			<Paragraph>{t('signatureAnnotationTextMessage')}</Paragraph>
			<StyledSignatureInputWrapper vertical justify="center">
				<StyledSignatureInput
					autoFocus
					data-testid="typed-signature-input"
					value={signatureText}
					onChange={onTextChange}
					ref={inputRef}
					maxLength={150}
					fontSize={fontSize}
					penColor={penColor}
					aria-label={t('signatureAnnotationTextMessage')}
				/>
			</StyledSignatureInputWrapper>
			<canvas
				ref={canvasRef}
				{...SignatureAreaDimensions}
				id="input-text-canvas"
				data-testid="typed-signature-canvas"
				style={{ display: 'none' }}
				aria-label={t('signatureAnnotationTextMessage')}
			></canvas>
			{isSignatureSigned !== SignatureState.NOT_SIGNED && (
				<ClearSignatureButton
					onClick={clearSignature}
					data-testid="signature-text-clear-link"
				/>
			)}
			<HiddenResizer ref={elementRef} fontSize={fontSize}>
				{savedSignatureText}
			</HiddenResizer>
		</StyledSignatureTypeWrapper>
	);
};
